import { __assign, __read, __rest, __spread } from "tslib";
import React, { useRef, useEffect, useState, useCallback, useMemo, } from "react";
import classNames from "classnames";
import { ChevronDownIcon } from "@tencent/tea-icons-react";
import { useDefaultValue } from "../form";
import { Popover } from "../popover";
import { Input } from "../input";
import { Icon } from "../icon";
import { Tag } from "../tag";
import { CascaderMenuBox } from "./CascadeMenu";
import { CascaderBox, getOptions } from "./CascaderBox";
import { CascaderSearch } from "./CascaderSearch";
import { TagSelectInput } from "../tagselect/TagSelectInput";
import { useDefault } from "../_util/use-default";
import { useConfig } from "../_util/config-context";
import { useTranslation } from "../i18n";
import { noop } from "../_util/noop";
import { mergeEventProps } from "../_util/merge-event-props";
import { forwardRefWithStatics } from "../_util/forward-ref-with-statics";
import { mergeRefs } from "../util";
import { defaultAllValue, getRelations, separator, } from "./CascadeMenu/CascaderMultiMenuLists";
import { getAllIds, getRelationMap, useValueMode, } from "../checktree/CheckTree";
var defaultValueRender = function (options) {
    var leaf = options[options.length - 1];
    return (leaf === null || leaf === void 0 ? void 0 : leaf.label) || (leaf === null || leaf === void 0 ? void 0 : leaf.value) || "-";
};
function isEqual(a, b) {
    return a.length === b.length && a.every(function (str, i) { return str === b[i]; });
}
export var CascaderMultiple = forwardRefWithStatics(function CascaderMultiple(props, ref) {
    var t = useTranslation();
    var classPrefix = useConfig().classPrefix;
    var _a = useDefaultValue(props, []), data = _a.data, rawValue = _a.value, rawOnChange = _a.onChange, onLoad = _a.onLoad, disabled = _a.disabled, style = _a.style, className = _a.className, _b = _a.placeholder, placeholder = _b === void 0 ? t.pleaseSelect : _b, _c = _a.defaultOpen, defaultOpen = _c === void 0 ? false : _c, open = _a.open, _d = _a.onOpenChange, onOpenChange = _d === void 0 ? noop : _d, clearable = _a.clearable, searchable = _a.searchable, filter = _a.filter, type = _a.type, expandTrigger = _a.expandTrigger, all = _a.all, _e = _a.valueRender, valueRender = _e === void 0 ? defaultValueRender : _e, _f = _a.valueMode, valueMode = _f === void 0 ? "onlyLeaf" : _f, _g = _a.showMode, showMode = _g === void 0 ? "onlyLeaf" : _g, tips = _a.tips, bottomTips = _a.bottomTips, _h = _a.onScrollBottom, onScrollBottom = _h === void 0 ? noop : _h, _j = _a.onSearch, onSearch = _j === void 0 ? noop : _j, 
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    multiple = _a.multiple, _k = _a.placement, placement = _k === void 0 ? "bottom-start" : _k, _l = _a.placementOffset, placementOffset = _l === void 0 ? 5 : _l, _m = _a.closeOnScroll, closeOnScroll = _m === void 0 ? true : _m, popoverProps = __rest(_a, ["data", "value", "onChange", "onLoad", "disabled", "style", "className", "placeholder", "defaultOpen", "open", "onOpenChange", "clearable", "searchable", "filter", "type", "expandTrigger", "all", "valueRender", "valueMode", "showMode", "tips", "bottomTips", "onScrollBottom", "onSearch", "multiple", "placement", "placementOffset", "closeOnScroll"]);
    var _o = __read(useState(false), 2), hover = _o[0], setHover = _o[1];
    var scheduleUpdateRef = useRef(null);
    useEffect(function () {
        if (scheduleUpdateRef.current) {
            scheduleUpdateRef.current();
        }
    }, [rawValue]);
    var _p = __read(useDefault(open, defaultOpen, onOpenChange), 2), isOpened = _p[0], setIsOpened = _p[1];
    var searchInputRef = useRef(null);
    var _q = __read(useState(""), 2), searchValue = _q[0], setSearchValue = _q[1];
    var _r = __read(useState(""), 2), fullSearchValue = _r[0], setFullSearchValue = _r[1];
    var _s = __read(useState(0), 2), wrapperWidth = _s[0], setWrapperWidth = _s[1];
    var Box = type === "menu" ? CascaderMenuBox : CascaderBox;
    /**
     * -----------
     * 值类型转换
     * -----------
     */
    var _t = useMemo(function () {
        var relations = getRelations(data, type === "menu" ? all : false).relations;
        return getRelationMap(relations);
    }, [all, data, type]), parentMap = _t.parentMap, childrenMap = _t.childrenMap;
    // 拼接多级值
    var _u = __read(useMemo(function () {
        var joinedValue = rawValue.map(function (list) { return list.join(separator); });
        var joinedOnChange = function (value, context) {
            rawOnChange(value.map(function (i) { return i.split(separator); }), {
                event: context === null || context === void 0 ? void 0 : context.event,
                options: value.map(function (i) { return getOptions(data, i.split(separator)); }),
            });
        };
        return [joinedValue, joinedOnChange];
    }, [data, rawOnChange, rawValue]), 2), joinedValue = _u[0], joinedOnChange = _u[1];
    // 只包含叶节点模式的拼接后的 value/onChange
    var _v = __read(useValueMode(joinedValue, joinedOnChange, parentMap, childrenMap, valueMode), 2), onlyLeafJoinedValue = _v[0], onlyLeafJoinedOnChange = _v[1];
    // 只包含叶节点模式的 value/onChange
    var _w = __read(useMemo(function () {
        var onlyLeafValue = onlyLeafJoinedValue.map(function (i) { return i.split(separator); });
        var onlyLeafOnChange = function (value) {
            var args = [];
            for (var _i = 1; _i < arguments.length; _i++) {
                args[_i - 1] = arguments[_i];
            }
            onlyLeafJoinedOnChange.apply(void 0, __spread([value.map(function (i) { return i.join(separator); })], args));
        };
        return [onlyLeafValue, onlyLeafOnChange];
    }, [onlyLeafJoinedOnChange, onlyLeafJoinedValue]), 2), onlyLeafValue = _w[0], onlyLeafOnChange = _w[1];
    var isSelected = useCallback(function (value) { return !!onlyLeafValue.find(function (v) { return isEqual(v, value); }); }, [onlyLeafValue]);
    var onSelect = function (value, context) {
        var next = __spread(onlyLeafValue, [value]);
        onlyLeafOnChange(next, __assign(__assign({}, context), { options: next.map(function (v) { return getOptions(data, v); }) }));
    };
    var onDeselect = function (value, context) {
        var next = onlyLeafValue.filter(function (v) { return !isEqual(v, value); });
        onlyLeafOnChange(next, __assign(__assign({}, context), { options: next.map(function (v) { return getOptions(data, v); }) }));
    };
    // showMode 转换渲染节点
    var tags = useMemo(function () {
        // 全选父节点 + 叶节点
        var allValue = getAllIds(onlyLeafJoinedValue, parentMap, childrenMap);
        if (showMode === "parentFirst") {
            var allSet_1 = new Set(allValue);
            var invalidSet_1 = new Set();
            allValue.forEach(function (id) {
                // 父节点此刻被选中则该节点无效
                if (parentMap.has(id) && allSet_1.has(parentMap.get(id))) {
                    invalidSet_1.add(id);
                }
            });
            // 转换后的 valueList
            var showValueList_1 = allValue
                .filter(function (id) { return !invalidSet_1.has(id); })
                .map(function (i) { return i.split(separator); });
            var optionsList_1 = showValueList_1
                .map(function (v) { return getOptions(data, v); })
                .filter(Boolean);
            return optionsList_1.map(function (options) {
                var _a, _b;
                var key = options.map(function (i) { return i.value; }).join(separator);
                // all 配置比较特殊，无法在 getOptions 中找到
                if (key === (((_a = all) === null || _a === void 0 ? void 0 : _a.value) || defaultAllValue)) {
                    options[0].label = ((_b = all) === null || _b === void 0 ? void 0 : _b.label) || t.selectAllText;
                }
                return (React.createElement(Tag, { key: key, onClose: function (event) {
                        event.stopPropagation();
                        if (disabled) {
                            return;
                        }
                        var next = onlyLeafValue.filter(function (value) {
                            var curKey = value.join(separator);
                            var result = true;
                            while (curKey) {
                                if (curKey === key) {
                                    result = false;
                                    break;
                                }
                                curKey = parentMap.get(curKey);
                            }
                            return result;
                        });
                        onlyLeafOnChange(next, {
                            event: event,
                            options: next.map(function (v) { return getOptions(data, v); }),
                        });
                    } }, valueRender(options)));
            });
        }
        // if (showMode === "onlyLeaf")
        // 转换后的 valueList
        var showValueList = allValue
            .filter(function (i) { return !childrenMap.has(i); })
            .map(function (i) { return i.split(separator); });
        var optionsList = showValueList
            .map(function (v) { return getOptions(data, v); })
            .filter(Boolean);
        return optionsList.map(function (options) {
            var key = options.map(function (i) { return i.value; }).join(separator);
            return (React.createElement(Tag, { key: key, onClose: function (event) {
                    event.stopPropagation();
                    if (disabled) {
                        return;
                    }
                    var next = onlyLeafValue.filter(function (value) { return value.join(separator) !== key; });
                    onlyLeafOnChange(next, {
                        event: event,
                        options: next.map(function (v) { return getOptions(data, v); }),
                    });
                } }, valueRender(options)));
        });
    }, [
        onlyLeafJoinedValue,
        parentMap,
        childrenMap,
        showMode,
        data,
        all,
        valueRender,
        t.selectAllText,
        disabled,
        onlyLeafValue,
        onlyLeafOnChange,
    ]);
    return (React.createElement(Popover, __assign({ trigger: disabled ? "empty" : "click", visible: isOpened, onVisibleChange: setIsOpened, onExited: function () {
            setSearchValue("");
            setFullSearchValue("");
        }, placement: placement, placementOffset: placementOffset, closeOnScroll: closeOnScroll }, popoverProps, { overlay: function (_a) {
            var scheduleUpdate = _a.scheduleUpdate;
            scheduleUpdateRef.current = scheduleUpdate;
            if (searchValue) {
                return (React.createElement(CascaderSearch, { multiple: true, data: data, searchValue: searchValue, filter: filter, changeOnSelect: false, isSelected: isSelected, onChange: function (value, _a) {
                        var selected = _a.selected, context = __rest(_a, ["selected"]);
                        if (selected) {
                            onSelect(value, context);
                        }
                        else {
                            onDeselect(value, context);
                        }
                    }, tips: tips, bottomTips: bottomTips, onScrollBottom: onScrollBottom }));
            }
            return (React.createElement(Box, { multiple: true, all: all, curValueList: onlyLeafValue, data: data, onChange: onlyLeafOnChange, onSelect: onSelect, onLoad: onLoad, onClose: function () { return setIsOpened(false); }, changeOnSelect: false, scheduleUpdate: scheduleUpdate, isSelected: isSelected, onDeselect: onDeselect, expandTrigger: expandTrigger, tips: tips, bottomTips: bottomTips, onScrollBottom: onScrollBottom }));
        } }),
        React.createElement("div", __assign({ className: classNames(classPrefix + "-cascader", className, {
                "is-disabled": disabled,
            }), style: style, ref: mergeRefs(ref, function (ref) { return ref && setWrapperWidth(ref.clientWidth); }) }, mergeEventProps(props, {
            onMouseEnter: function () { return setHover(true); },
            onMouseLeave: function () { return setHover(false); },
        })),
            React.createElement("div", { className: classPrefix + "-cascader__input", onClick: function (event) {
                    if (disabled) {
                        return;
                    }
                    if (searchable && searchInputRef.current) {
                        event.stopPropagation();
                        searchInputRef.current.focus();
                        setIsOpened(true);
                    }
                } },
                React.createElement("div", { className: classPrefix + "-cascader__value" },
                    React.createElement(Tag.Group, null,
                        tags,
                        searchable && (React.createElement(TagSelectInput, { disabled: disabled, className: classPrefix + "-input--editor", ref: searchInputRef, maxWidth: wrapperWidth, reservedWidth: 2, value: searchValue, onChange: function (value) {
                                if (typeof scheduleUpdateRef.current === "function") {
                                    // 改变搜索内容时会导致浮层高度变化, 需要更新浮层位置
                                    scheduleUpdateRef.current();
                                }
                                setSearchValue(value);
                                onSearch(value);
                            }, onInput: function (event) {
                                setFullSearchValue(event.currentTarget.value);
                            } })))),
                !fullSearchValue && (React.createElement(Input, { readonly: true, autoComplete: "off", placeholder: onlyLeafValue.length ? "" : placeholder }))),
            clearable && !disabled && hover && onlyLeafValue.length ? (React.createElement(Icon, { type: "dismiss", onClick: function (event) {
                    event.stopPropagation();
                    onlyLeafOnChange([], { event: event, options: [] });
                } })) : (React.createElement(ChevronDownIcon, null)))));
}, {
    defaultLabelAlign: "middle",
});
CascaderMultiple.displayName = "CascaderMultiple";
//# sourceMappingURL=CascaderMultiple.js.map